/**
 * Pure path helpers for the shell navigation (port of GolfTec `lib/nav-paths.ts`).
 *
 * No React and no icons, so the rail, the mobile sheet, the header and the
 * palette share one rule.
 */

/**
 * Paths that light another nav item (plans/03 A5.1): the shelter check-in form
 * belongs to Impact Checks and receipts to Relief Deliveries.
 */
const ACTIVE_ALIASES: ReadonlyArray<{ prefix: string; href: string }> = [
  { prefix: '/checkin', href: '/checks' },
  { prefix: '/receipt', href: '/deliveries' },
]

function underPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/**
 * Prefix match, so nested routes keep their nav item lit: `/workbench/WB-101`
 * lights the Auto Workbench. `/` matches only itself.
 */
export function isNavItemActive(pathname: string | null | undefined, href: string): boolean {
  if (!pathname) return false
  const queryAt = href.indexOf('?')
  const hrefPath = queryAt === -1 ? href : href.slice(0, queryAt)
  if (hrefPath === '/') return pathname === '/'
  if (underPrefix(pathname, hrefPath)) return true
  return ACTIVE_ALIASES.some((alias) => alias.href === hrefPath && underPrefix(pathname, alias.prefix))
}

export interface Crumb {
  label: string
  href: string
}

/**
 * Labels for path segments that are not a nav entry of their own. Every other
 * segment is shown as written (record ids such as WB-101 stay upper case).
 */
export const SEGMENT_LABELS: Readonly<Record<string, string>> = {
  ai: 'Auto Intelligence',
  admin: 'Administration',
  destination: 'Destination',
  receipt: 'Receipt',
  checkin: 'Shelter check-in form',
}

/** `decodeURIComponent` that returns the input unchanged for a malformed escape instead of throwing. */
export function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function segmentLabel(segment: string): string {
  const decoded = safeDecode(segment)
  return SEGMENT_LABELS[decoded] ?? decoded.charAt(0).toUpperCase() + decoded.slice(1).replace(/-/g, ' ')
}

/**
 * The header breadcrumb: one crumb per accumulated path after the product
 * name. Each crumb takes the nav label for its exact href when there is one,
 * so the breadcrumb and the rail say the same thing.
 */
export function breadcrumbTrail(
  pathname: string | null | undefined,
  labelFor: (href: string) => string | undefined,
  rootLabel: string
): Crumb[] {
  const segments = (pathname ?? '/').split('/').filter(Boolean)
  if (segments.length === 0) return [{ label: labelFor('/') ?? rootLabel, href: '/' }]
  const trail: Crumb[] = []
  let current = ''
  for (const segment of segments) {
    current += `/${segment}`
    trail.push({ label: labelFor(current) ?? segmentLabel(segment), href: current })
  }
  return trail
}

// ─── Page-provided crumb labels ────────────────────────────────────────────
//
// A record page names its own crumb once it has loaded
// (`setCrumbLabel('/workbench/WB-101', 'WB-101')`). The header subscribes, so
// the label appears without a navigation. Cleared when the page unmounts.

const crumbOverrides = new Map<string, string>()
const crumbListeners = new Set<() => void>()
let crumbVersion = 0

/** Set (or with `null` clear) the crumb label for one exact href. */
export function setCrumbLabel(href: string, label: string | null): void {
  const text = label?.trim() || null
  if ((crumbOverrides.get(href) ?? null) === text) return
  if (text) crumbOverrides.set(href, text)
  else crumbOverrides.delete(href)
  crumbVersion += 1
  crumbListeners.forEach((listener) => listener())
}

export function crumbLabelFor(href: string): string | undefined {
  return crumbOverrides.get(href)
}

export function subscribeCrumbLabels(listener: () => void): () => void {
  crumbListeners.add(listener)
  return () => {
    crumbListeners.delete(listener)
  }
}

export function crumbLabelsVersion(): number {
  return crumbVersion
}
